import {
  BaseMutationOptions,
  CreateGroupClientData,
  CreateGroupRequest,
  GetGroupsRequest,
  GroupFilters,
  ResolverMutationRequestMetadata,
  SoftDeleteGroupClientData,
  SoftDeleteGroupRequest,
  UpdateGroupClientData,
  UpdateGroupRequest,
  parseResolverResponse
} from '@esg/syntiro-api-core';
import { DocumentNode } from 'graphql';
import { GET_GROUPS_COMPANIES } from '../queries/group.queries';
import { Group } from '@esg/esg-global-types';
import { apollo_client_v2 } from '../../App';
import { FetchResult } from '@apollo/client';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { auth } from '../../lib/google/firebase';
import { CREATE_GROUP, SOFT_DELETE_GROUP, UPDATE_GROUP } from '../mutations/group.mutations';
import {
  isValidBaseReference,
  isValidBaseReferenceArray,
  isValidBoolean,
  validateParams
} from '../validators/base.validator';
import {
  isValidGroupId,
  isValidGroupName,
  isValidGroupSectors
} from '../validators/group.validator';

const SOURCE = 'group.service.ts';

/**
 * Query all groups.
 * @param {GroupFilters} filters Filter parameters to use for groups query.
 * @param {DocumentNode | undefined} query Optionally pass in specific query to use for desired company fields.
 * @returns {Promise<Array<CompanyExtended>>}
 */
export const getGroups = async (
  filters: GroupFilters = {},
  query: DocumentNode = GET_GROUPS_COMPANIES
): Promise<Array<Group>> => {
  const input: GetGroupsRequest['input'] = {
    metadata: {},
    filter: {
      deleted: false,
      ...filters
    }
  };
  return apollo_client_v2
    .query({
      variables: { input },
      query: query,
      fetchPolicy: 'no-cache'
    })
    .then((response: FetchResult) => {
      if (!response.data?.getGroups) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('getGroups'));
      }
      const api_result: Array<Group> | null | undefined = parseResolverResponse<Array<Group>>(
        response.data.getGroups
      );
      return api_result ? api_result : [];
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getGroups'),
        { filters, query },
        tracking_id
      );
    });
};

/**
 * Create a group in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {CreateGroupClientData} data Required data to populate company fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<Company | null | undefined>}
 */
export const createGroup = async (
  metadata: ResolverMutationRequestMetadata = {},
  data: CreateGroupClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Group | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'data.name': {
        validate: isValidGroupName,
        message: 'Invalid name specified.'
      },
      'data.sectors': {
        validate: isValidGroupSectors,
        message: 'Invalid sectors specified.'
      },
      'data.enterprise_classification': {
        validate: isValidBaseReference,
        message: 'Invalid enterprise classification specified.'
      },
      'data.guided_readiness': {
        validate: isValidBoolean,
        message: 'Invalid guided readiness specified.'
      }
    }
  );
  const input: CreateGroupRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      group: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: CREATE_GROUP,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.createGroup) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('createGroup'));
      }
      return parseResolverResponse<Group>(response.data.createGroup);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createGroup'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Update a group in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {UpdateGroupClientData} data Required data to populate group fields.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<Company | null | undefined>}
 */
export const updateGroup = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateGroupClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Group | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'data.id': {
        validate: isValidGroupId,
        message: 'Invalid group id specified.'
      },
      'data.name': {
        validate: isValidGroupName,
        message: 'Invalid group name specified.',
        is_optional: true
      },
      'data.sectors': {
        validate: isValidBaseReferenceArray,
        message: 'Invalid sectors specified.',
        is_optional: true
      },
      'data.enterprise_classification': {
        validate: isValidBaseReference,
        message: 'Invalid enterprise classification specified.',
        is_optional: true
      },
      'data.guided_readiness': {
        validate: isValidBoolean,
        message: 'Invalid guided readiness specified.',
        is_optional: true
      }
    }
  );

  const input: UpdateGroupRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      group: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: UPDATE_GROUP,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.updateGroup) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateGroup'));
      }
      return parseResolverResponse<Group>(response.data.updateGroup);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateGroup'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Soft delete group in database from required request data.
 * @param {ResolverMutationRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {SoftDeleteGroupClientData} data Required data to delete a group.
 * @param {BaseMutationOptions | undefined} options Standard mutator options to pass to the API.
 * @returns {Promise<Group | null | undefined>}
 */
export const softDeleteGroup = async (
  metadata: ResolverMutationRequestMetadata,
  data: SoftDeleteGroupClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<Group | null | undefined> => {
  validateParams(
    { metadata, data },
    {
      'data.id': {
        validate: isValidGroupId,
        message: 'Invalid group id specified.'
      }
    }
  );

  const input: SoftDeleteGroupRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      group: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: SOFT_DELETE_GROUP,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.softDeleteGroup) {
        throw new Error('No data returned');
      }
      return parseResolverResponse<Group>(response.data.softDeleteGroup);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'softDeleteGroup'),
        { metadata, data },
        tracking_id
      );
    });
};
